import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { ColDef, GetRowIdParams, RowClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import TimeAgo from 'react-timeago';
import { useJobs } from '../hooks/useJobs';
import { useApiFetch } from '../hooks/useApiFetch';
import { useAuthContext } from '../contexts/AuthContext';
import type {
  JobListItem,
  JobsFilterState,
  JobsScope,
  JobStatusCounts,
} from '../types/jobs';
import { type JobStatus, isTerminal } from '../types/monitoring';
import { isLongRunning } from '../utils/jobStatus';
import { ErrorDisplay } from './ErrorDisplay';
import { Layout } from './Layout';
import { CopyButtonCellRenderer } from './ag-grid/cellRenderers';
import './ag-grid/styles.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;

// Shared with the grid via AgGrid's `context` so cell renderers can refetch the
// list and know the caller (to gate owner-only actions) without prop drilling
// through AG Grid.
interface JobsGridContext {
  refetch: () => void;
  callerSub: string | null;
}

const STATUS_CONFIG: Record<JobStatus, { bg: string; color: string }> = {
  pending: { bg: '#f3f4f6', color: '#6b7280' },
  running: { bg: '#dcfce7', color: '#166534' },
  waiting_for_capacity: { bg: '#dbeafe', color: '#1e40af' },
  importing: { bg: '#fef9c3', color: '#854d0e' },
  stuck: { bg: '#ffedd5', color: '#9a3412' },
  complete: { bg: '#dcfce7', color: '#166534' },
  failed: { bg: '#fce7e7', color: '#991b1b' },
  deleted: { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_MARK: Partial<Record<JobStatus, string>> = {
  running: '● ',
  waiting_for_capacity: '● ',
  importing: '● ',
  complete: '✓ ',
  failed: '✗ ',
  stuck: '⚠ ',
  deleted: '🗑 ',
};

// Status order for the filter chips — active states first so a user scanning
// the bar sees in-flight work before terminal piles.
const STATUS_ORDER: JobStatus[] = [
  'running',
  'waiting_for_capacity',
  'stuck',
  'pending',
  'importing',
  'failed',
  'complete',
  'deleted',
];

// Humanize a status enum for display, e.g. waiting_for_capacity -> "waiting for
// capacity". Used everywhere a status is shown so badge/chips/empty-state agree.
const formatStatus = (status: JobStatus): string => status.replace(/_/g, ' ');

function JobTypeBadge({ value }: { value: JobListItem['job_type'] }) {
  const isEvalSet = value === 'eval-set';
  return (
    <span
      style={{
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '3px',
        backgroundColor: isEvalSet ? '#dcfce7' : '#dbeafe',
        color: isEvalSet ? '#166534' : '#1e40af',
        fontWeight: 600,
      }}
    >
      {isEvalSet ? 'Eval set' : 'Scan'}
    </span>
  );
}

function StatusBadge({ status }: { status: JobStatus | null }) {
  if (status === null) {
    return (
      <span
        style={{
          fontSize: '11px',
          padding: '2px 8px',
          borderRadius: '10px',
          backgroundColor: '#f3f4f6',
          color: '#6b7280',
        }}
      >
        unknown
      </span>
    );
  }
  const c = STATUS_CONFIG[status];
  return (
    <span
      title={
        status === 'deleted'
          ? 'Torn down before producing any output. Deleted jobs that produced results keep showing their outcome.'
          : undefined
      }
      style={{
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: c.bg,
        color: c.color,
      }}
    >
      {STATUS_MARK[status] ?? ''}
      {formatStatus(status)}
    </span>
  );
}

function ScopeToggle({
  scope,
  onChange,
}: {
  scope: JobsScope;
  onChange: (next: JobsScope) => void;
}) {
  const options: { value: JobsScope; label: string }[] = [
    { value: 'mine', label: 'Mine' },
    { value: 'all', label: 'All' },
  ];
  return (
    <div className="inline-flex h-8 rounded border border-gray-300 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-900">
      {options.map(opt => {
        const isActive = scope === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              if (!isActive) onChange(opt.value);
            }}
            className={`px-3 text-xs font-medium transition-colors ${
              isActive
                ? 'bg-brand-600 text-white dark:bg-brand-500'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
            aria-pressed={isActive}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StatusFilterBar({
  counts,
  total,
  active,
  onSelect,
}: {
  counts: JobStatusCounts;
  total: number;
  active: JobStatus | null;
  onSelect: (status: JobStatus | null) => void;
}) {
  const present = STATUS_ORDER.filter(s => (counts[s] ?? 0) > 0);
  const chip = (
    key: string,
    label: string,
    count: number,
    isActive: boolean,
    onClick: () => void,
    color?: { bg: string; color: string }
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        isActive
          ? 'border-brand-500 ring-1 ring-brand-400 dark:border-brand-400'
          : 'border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800'
      }`}
      style={
        color ? { backgroundColor: color.bg, color: color.color } : undefined
      }
    >
      <span>{label}</span>
      <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {chip('all', 'All', total, active === null, () => onSelect(null))}
      {present.map(s =>
        chip(
          s,
          formatStatus(s),
          counts[s] ?? 0,
          active === s,
          () => onSelect(active === s ? null : s),
          STATUS_CONFIG[s]
        )
      )}
    </div>
  );
}

// Chip flagging samples parked on a human approval. Links to the run's live
// status page — the same destination as the status badge — so an on-call can
// jump straight to what's parked. Colored like the "importing" status (amber).
export function PendingApprovalChip({
  jobId,
  count,
}: {
  jobId: string;
  count: number;
}) {
  return (
    <Link
      to={`/eval-set/${encodeURIComponent(jobId)}/status`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      title="Samples parked waiting on a human approval. Click to view status."
      style={{
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      ⏸ {count} waiting
    </Link>
  );
}

export function StatusCell({ data }: { data: JobListItem }) {
  const badge = <StatusBadge status={data.status} />;
  const pending = data.pending_interactions ?? 0;

  // Make the badge a shortcut to the live status page, but only for in-flight
  // eval-sets — once complete/failed there's nothing live to watch.
  const linkable =
    data.job_type === 'eval-set' &&
    data.status !== null &&
    !isTerminal(data.status);

  const statusNode = linkable ? (
    <Link
      to={`/eval-set/${encodeURIComponent(data.job_id)}/status`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      title="View live status"
      className="group inline-flex items-center gap-1.5"
    >
      <span className="rounded group-hover:ring-2 group-hover:ring-emerald-200">
        {badge}
      </span>
      <span
        aria-hidden="true"
        className="text-xs text-gray-400 group-hover:text-emerald-700"
      >
        ↗
      </span>
    </Link>
  ) : (
    badge
  );

  return (
    <span className="flex h-full w-full items-center gap-1.5">
      {statusNode}
      {pending > 0 && (
        <PendingApprovalChip jobId={data.job_id} count={pending} />
      )}
    </span>
  );
}

function AgeCell({ data }: { data: JobListItem }) {
  if (!data.created_at) return <span>-</span>;
  const longRunning = isLongRunning(data);
  return (
    <span
      className="flex h-full w-full items-center gap-1.5"
      title={new Date(data.created_at).toLocaleString()}
    >
      {longRunning && (
        <span title="Running for over 24h" aria-label="long-running">
          ⏰
        </span>
      )}
      <TimeAgo date={data.created_at} />
    </span>
  );
}

function RowActions({
  data,
  context,
}: {
  data: JobListItem;
  context: JobsGridContext;
}) {
  const { apiFetch } = useApiFetch();
  const [busy, setBusy] = useState<'stop' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stop/Delete are eval-set + owner-only on the API. Show them only on your
  // own non-terminal eval-sets — gating on the real owner (created_by) rather
  // than the list scope, so terminal rows and others' jobs stay quiet instead
  // of inviting a guaranteed 403.
  const eligible =
    data.job_type === 'eval-set' &&
    context.callerSub !== null &&
    data.created_by === context.callerSub &&
    data.status !== null &&
    !isTerminal(data.status);

  const act = async (
    kind: 'stop' | 'delete',
    confirmMsg: string,
    path: string,
    method: 'POST' | 'DELETE'
  ) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(kind);
    setError(null);
    try {
      const res = await apiFetch(
        path,
        { method },
        { returnNonOkResponse: true }
      );
      if (res && res.ok) {
        context.refetch();
        return;
      }
      setError(
        res?.status === 403
          ? 'Not permitted'
          : `${kind === 'stop' ? 'Stop' : 'Force stop'} failed${res ? ` (${res.status})` : ''}`
      );
    } catch {
      // Network error / abort: apiFetch rejects rather than returning a
      // response. Surface it and (via finally) re-enable the buttons.
      setError(`${kind === 'stop' ? 'Stop' : 'Force stop'} failed`);
    } finally {
      setBusy(null);
    }
  };

  const id = encodeURIComponent(data.job_id);
  // Stop/Delete (when eligible) sit left of an always-present View link, all
  // right-aligned in one column so terminal rows just show "View →" with no
  // empty Actions gap.
  return (
    <span className="flex h-full w-full items-center justify-end gap-1.5">
      {error && (
        <span className="text-[11px] text-red-600 dark:text-red-400">
          {error}
        </span>
      )}
      {eligible && (
        <>
          <button
            type="button"
            disabled={busy !== null}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              void act(
                'stop',
                'Stop this eval set? Active samples are scored with partial work and the run exits.',
                `/eval_sets/${id}/stop`,
                'POST'
              );
            }}
            className="rounded border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:bg-gray-900 dark:text-amber-300 dark:hover:bg-amber-950/40"
          >
            {busy === 'stop' ? 'Stopping…' : 'Stop'}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              void act(
                'delete',
                'Force stop this eval set? This kills the runner and tears down its Kubernetes resources immediately. Logs and results are kept.',
                `/eval_sets/${id}`,
                'DELETE'
              );
            }}
            className="rounded border border-red-300 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-gray-900 dark:text-red-400 dark:hover:bg-red-950/40"
          >
            {busy === 'delete' ? 'Stopping…' : 'Force stop'}
          </button>
        </>
      )}
      <ViewLink data={data} />
    </span>
  );
}

function ViewLink({ data }: { data: JobListItem }) {
  const isEvalSet = data.job_type === 'eval-set';
  const href = isEvalSet
    ? `/eval-set/${encodeURIComponent(data.job_id)}`
    : `/scan/${encodeURIComponent(data.job_id)}`;
  return (
    <span className="flex items-center gap-3">
      <a
        href={href}
        className="text-brand-600 hover:text-brand-700 text-xs font-medium"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        View →
      </a>
      {isEvalSet && (
        <Link
          to={`/eval-set/${encodeURIComponent(data.job_id)}/timeline`}
          className="text-brand-600 hover:text-brand-700 text-xs font-medium"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          Timeline
        </Link>
      )}
    </span>
  );
}

interface JobsListProps {
  filters: JobsFilterState;
  setFilters: (partial: Partial<JobsFilterState>) => void;
}

export function JobsList({ filters, setFilters }: JobsListProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthContext();
  const callerSub = user?.sub ?? null;

  const { jobs, isLoading, error, total, statusCounts, refetch } = useJobs({
    scope: filters.scope,
    page: filters.page,
    limit: PAGE_SIZE,
    search: filters.search,
    status: filters.status,
    waiting: filters.waiting,
  });

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ page: newPage });
    },
    [setFilters]
  );

  // The URL `?page=` can outrun reality if results shrink (job deleted, scope
  // narrowed, search narrowed). Clamp for render math and reconcile the URL
  // once the data has actually arrived so the pagination controls don't lie.
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const displayPage = Math.min(filters.page, totalPages);

  useEffect(() => {
    if (!isLoading && filters.page > totalPages) {
      setFilters({ page: totalPages });
    }
  }, [isLoading, filters.page, totalPages, setFilters]);

  const gridContext = useMemo<JobsGridContext>(
    () => ({ refetch, callerSub }),
    [refetch, callerSub]
  );

  // "Waiting on approval" is a server-side filter (forwarded to /meta/jobs as
  // `waiting=1`), applied before pagination so `total`, the page slice, and the
  // rows agree. Parked runs are long-lived — their old created_at sorts them
  // onto later pages — so a page-local filter would hide the very runs an
  // on-call (scope=all) is looking for. We render the server rows directly.
  const columnDefs = useMemo<ColDef<JobListItem>[]>(
    () => [
      {
        field: 'job_type',
        headerName: 'Type',
        width: 90,
        sortable: false,
        cellRenderer: JobTypeBadge,
      },
      {
        field: 'job_id',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        cellRenderer: CopyButtonCellRenderer,
      },
      {
        headerName: 'Status',
        width: 210,
        sortable: false,
        cellRenderer: StatusCell,
      },
      {
        field: 'created_at',
        headerName: 'Age',
        width: 170,
        cellRenderer: AgeCell,
      },
      {
        headerName: 'Actions',
        width: 230,
        sortable: false,
        cellRenderer: RowActions,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<JobListItem>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<JobListItem>) => params.data.job_id,
    []
  );

  const rowClassRules = useMemo(
    () => ({
      'jobs-row-long-running': (p: RowClassParams<JobListItem>) =>
        p.data != null && isLongRunning(p.data),
    }),
    []
  );

  if (error) {
    return (
      <Layout>
        <ErrorDisplay message={error.toString()} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        {/* Toolbar */}
        <div className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-4 py-2 shrink-0 space-y-2">
          <form
            onSubmit={e => e.preventDefault()}
            className="flex items-center gap-3"
          >
            <ScopeToggle
              scope={filters.scope}
              onChange={next => setFilters({ scope: next })}
            />
            <div className="flex-1 relative max-w-md">
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search jobs..."
                value={filters.search}
                onChange={e => {
                  setFilters({ search: e.target.value });
                }}
                className="w-full h-8 px-3 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 bg-white text-gray-900 placeholder:text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-brand-300 dark:focus:border-brand-300"
              />
              {isLoading && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 dark:border-gray-700 border-t-brand-600 dark:border-t-brand-300 rounded-full"></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                refetch();
              }}
              className="h-8 px-4 text-sm font-medium rounded transition-colors text-gray-700 hover:bg-gray-200 border border-gray-300 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              onClick={() => setFilters({ waiting: !filters.waiting })}
              aria-pressed={filters.waiting}
              title="Show only runs with samples parked on a human approval"
              className={`h-8 px-3 text-xs font-medium rounded border transition-colors ${
                filters.waiting
                  ? 'border-brand-500 bg-brand-600 text-white dark:bg-brand-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              ⏸ Waiting on approval
            </button>
          </form>
          <StatusFilterBar
            counts={statusCounts}
            total={Object.values(statusCounts).reduce((a, b) => a + b, 0)}
            active={filters.status}
            onSelect={next => setFilters({ status: next })}
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-hidden relative">
          {isLoading ? (
            <div className="p-4">
              <div className="space-y-2">
                {Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-12"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-48"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-32"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {filters.waiting
                ? 'No jobs are waiting on an approval.'
                : filters.search
                  ? `No jobs found matching "${filters.search}"`
                  : filters.status
                    ? `No ${formatStatus(filters.status)} jobs.`
                    : filters.scope === 'mine'
                      ? "You haven't launched any jobs yet."
                      : 'No jobs match your filters.'}
            </div>
          ) : (
            <div className="ag-theme-quartz h-full w-full">
              <AgGridReact<JobListItem>
                rowData={jobs}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                context={gridContext}
                rowClassRules={rowClassRules}
                animateRows={false}
                suppressCellFocus={true}
                domLayout="normal"
              />
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 px-4 py-2 flex items-center justify-between shrink-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {(displayPage - 1) * PAGE_SIZE + 1}–
              {Math.min(displayPage * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(displayPage - 1)}
                disabled={displayPage === 1 || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  displayPage === 1 || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                ← Prev
              </button>
              <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                {displayPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(displayPage + 1)}
                disabled={displayPage === totalPages || isLoading}
                className={`h-7 px-3 text-xs font-medium rounded ${
                  displayPage === totalPages || isLoading
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
